import { createSlice } from '@reduxjs/toolkit'
import { actions as localActionsF } from './local'
import { actions as questionActionsF } from './questions'
import { actions as answerActionsF } from './answers'

import { deepSnakeCase } from 'Shared/functions/util'
import renderContext from './lib/renderContext'

const reducer = createSlice({
  name: 'taker',
  initialState: {
    flow: {
      actions: []
    },
    flowTaker: {},
    context: {
      template: {},
      settings: {},
      flow: {},
      client: {}
    }
  },
  reducers: {
    set: (s, a) => a.payload,
    upsert: (s, a) => ({ ...s, ...a.payload })
  },
}).reducer

const actions = (dis, store, restClient) => {
  const localActions = localActionsF(dis, store, restClient)
  const questionsActions = questionActionsF(dis, store, restClient)
  const answersActions = answerActionsF(dis, store, restClient)

  function load(flowId, { forget }) {
    const params = _.pickBy({ forget }, value => !!value)
    return restClient.get(`/api/target/flows/${flowId}`, { params })
      .then(({ data }) => {
        const { flow: camelFlow, flowTaker, targetDetails } = data
        const templateFields = deepSnakeCase(camelFlow.templateFields)
        const flowFields = deepSnakeCase(camelFlow.flowFields)
        const flow = { ...camelFlow, templateFields, flowFields }

        const context = renderContext({ flow, flowTaker })
        dis({ type: 'taker/set', payload: { flow, flowTaker, targetDetails, context } })

        const surveys = _.compact(flow.actions.map(a => a.survey))

        const questions = _.flatMap(surveys, s => s.questions)
        const answers = _.flatMap(questions, q => q.answers)

        questionsActions.setAll(questions)
        answersActions.setAll(answers)
      })
  }

  function submitPage({ pageNumber, actionId, responses }) {
    const flowTakerId = store.getState().taker.flowTaker.id
    const flow = store.getState().taker.flow
    const flowId = flow.id

    return restClient.post(`/api/target/flows/${flowId}/answer_survey`, { pageNumber, actionId, responses, flowTakerId })
      .then(({ data }) => {
        const { flowTaker } = data
        dis({ type: 'taker/upsert', payload: { flowTaker, context: renderContext({ flow, flowTaker }) } })
      })
  }

  function reportShare({ actionId, shareChannel }) {
    const flowTakerId = store.getState().taker.flowTaker.id
    const flow = store.getState().taker.flow
    const flowId = flow.id

    return restClient.post(`/api/target/flows/${flowId}/report_share`, { actionId, shareChannel, flowTakerId })
  }

  function updateNoTarget() {
    const flowTakerId = store.getState().taker.flowTaker.id
    const flow = store.getState().taker.flow
    const flowId = flow.id

    return restClient.post(`/api/target/flows/${flowId}/update_no_target`, { flowTakerId })
      .then(({ data }) => {
        const { flowTaker } = data
        dis({ type: 'taker/upsert', payload: { flowTaker, context: renderContext({ flow, flowTaker }), targetDetails: { signedIn: false } } })
      })
  }

  function completeAction({ actionId, actionParams = {} }) {
    const flowTakerId = store.getState().taker.flowTaker.id
    const flow = store.getState().taker.flow
    const flowId = flow.id
    const params = { actionId, flowTakerId }
    const action = flow.actions.find(a => a.id === actionId)
    if (action.kind === 'donate') {
      if (actionParams.stripeSessionId) {
        params.stripeSessionId = actionParams.stripeSessionId
      } else if (actionParams.stripePaymentIntentId) {
        params.stripePaymentIntentId = actionParams.stripePaymentIntentId
      }
    } else if (action.kind === 'email_to_target') {
      params.subject = actionParams.subject
      params.body = actionParams.body
      params.decisionMakerIds = actionParams.decisionMakerIds
      params.name = actionParams.name
      params.email = actionParams.email
      params.postcode = actionParams.postcode
      params.addressLine1 = actionParams.addressLine1
      params.town = actionParams.town
    }

    return restClient.post(`/api/target/flows/${flowId}/complete_action`, params)
      .then(({ data }) => {
        const { flowTaker } = data
        dis({ type: 'taker/upsert', payload: { flowTaker, context: renderContext({ flow, flowTaker }) } })
      })
  }

  function skipAction({ actionId }) {
    const flowTakerId = store.getState().taker.flowTaker.id
    const flow = store.getState().taker.flow
    const flowId = flow.id

    return restClient.post(`/api/target/flows/${flowId}/skip_action`, { actionId, flowTakerId })
      .then(({ data }) => {
        const { flowTaker } = data
        dis({ type: 'taker/upsert', payload: { flowTaker, context: renderContext({ flow, flowTaker }) } })
      })
  }

  function createStripeCheckoutSession({ amountInMinorUnits, isMonthly, coverFee }) {
    const flowTakerId = store.getState().taker.flowTaker.id
    const flow = store.getState().taker.flow
    const flowId = flow.id

    return restClient.post(`/api/target/stripe/checkout_session`, { amountInMinorUnits, isMonthly, coverFee, flowTakerId, flowId })
  }

  function createStripePaymentIntent({ amountInMinorUnits, isMonthly, coverFee }) {
    const flowTakerId = store.getState().taker.flowTaker.id
    const flow = store.getState().taker.flow
    const flowId = flow.id

    return restClient.post(`/api/target/stripe/payment_intent`, {
      amountInMinorUnits, isMonthly, coverFee, flowTakerId, flowId
    })
  }

  return {
    load,
    submitPage,
    reportShare,
    updateNoTarget,
    completeAction,
    skipAction,
    set: (payload) => {
      dis({ type: 'taker/set', payload })
    },
    createStripeCheckoutSession,
    createStripePaymentIntent
  }
}

export default { reducer, actions }
